//@flow
import * as React from 'react';
import * as Immutable from 'immutable';
import { CreateTerritory } from 'common/models/internal/CreateTerritoryPlan';
import { MasterOutlet } from 'common/models/internal/MasterOutlet';
import { useSelector } from 'react-redux';
import OutletSelectors from 'common/redux/selectors/outlet';
import { useParams } from 'react-router-dom';
import OutletSelection from 'common/entity/territoryMaster/territoryCreation/automatic/steps/OutletSelection';
import { TerritoryAddEditContext } from 'common/entity/territoryMaster/territoryCreation/TerritoryAddEditProvider';
import DifferentiableColorPicker from 'common/utils/DifferentiableColorPicker';
import TerritoryActions from 'common/redux/actions/territoryMaster';
import { useToast } from '@locus-taxy/neo-ui';
import { useTranslation } from 'react-i18next';
import { TerritoryOutletId } from 'common/models/internal/MasterTerritoryPlan';

type Props = {
    children: React.Node,
    territories: Immutable.List<CreateTerritory>,
    planName: string,
    isEditing: boolean,
    isManualPlan: boolean,
    planStatus: string,
    clientId: string
};

export const TerritoryContext: React$Context<{
    onOutletSelect: (index: string) => void,
    onOutletHover: (index: string) => void,
    onTerritorySelect: (id: string) => void,
    selectedOutletId: ?string,
    selectedTerritoryId: ?string,
    selectedOutlets: Immutable.List<MasterOutlet>,
    allOutlets: Immutable.List<MasterOutlet>,
    allOutletIds: Immutable.List<string>,
    isEditing: boolean,
    isManualPlan: boolean,
    unallocatedOutlets: Immutable.List<MasterOutlet>,
    hoveredTerritoryId: ?string,
    setHoveredTerritoryId: (id: ?string) => void,
    layerColorMap: Immutable.Map<string, string> | null,
    territories: Immutable.List<CreateTerritory>,
    toggleAddOutletsModel: () => void,
    selectedOutletMetrics: ?{
        count: number,
        totalRevenue: number,
        totalTat: string
    },
    clientId: string,
    hoveredOutletId?: string,
    changeTerritoryName: (id: string, name: string) => void,
    onTerritoryHover: (id: string) => void,
    setSelectedOutletId: (id: string) => void,
    selectedTerritoryIndex: number,
    setTerritories: (territories: Immutable.List<CreateTerritory>) => void,
    setHoveredOutletId: (id: string) => void,
    setUnallocatedOutlets: (outlets: Immutable.List<MasterOutlet>) => void,
    setLayerColorMap: (colors: Immutable.Map<string, string>) => void,
    colorPicker: typeof DifferentiableColorPicker,
    changeTerritory: (
        originalTerritoryId: string,
        newTerritoryId: string,
        outletId: string,
        unAllocatedOutlet?: MasterOutlet
    ) => void,
    planStatus: string,
    ...
}> = React.createContext({});

const ManualTerritoryProvider = (props: Props): React.Node => {
    const { showToast } = useToast();
    const { t } = useTranslation('entityManagement');
    const [territories, setTerritories] = React.useState(props.territories);
    const [selectedTerritoryId, setSelectedTerritoryId] = React.useState(
        territories.size ? territories.first().id : null
    );
    const [hoveredTerritoryId, setHoveredTerritoryId] = React.useState(null);
    const [unallocatedOutlets, setUnallocatedOutlets] = React.useState(
        Immutable.List()
    );
    const [colorPicker] = React.useState(new DifferentiableColorPicker());

    const [selectedTerritoryIndex, setSelectedTerritoryIndex] = React.useState(
        null
    );
    const [selectedOutletMetrics, setSelectedOutletMetrics] = React.useState(
        null
    );

    const [layerColorMap, setLayerColorMap] = React.useState(() => {
        const colorMap = territories.size
            ? territories.reduce((colorMap, territory) => {
                  const color = colorPicker.pick().code;
                  const updatedColorMap = colorMap.set(
                      territory.get('id'),
                      color
                  );
                  return updatedColorMap;
              }, Immutable.Map())
            : null;
        return colorMap;
    });

    const [selectedOutletId, setSelectedOutletId] = React.useState(
        territories.size
            ? territories.first().outletsInfo.outletIds.first()?.outletId
            : null
    );

    const [hoveredOutletId, setHoveredOutletId] = React.useState(null);

    const { disableNavigation, onDismissTerritoryCreation } = React.useContext(
        TerritoryAddEditContext
    );

    const changeTerritoryName = React.useCallback(
        (territoryId, value) => {
            let editedTerritoryIndex;
            const selectedTerritory = territories.find((territory, index) => {
                if (territoryId === territory.id) {
                    editedTerritoryIndex = index;
                    return true;
                }
                return false;
            });
            const updatedTerritory = selectedTerritory?.setName(value);
            const updatedTerritories =
                editedTerritoryIndex !== undefined && updatedTerritory
                    ? territories.set(editedTerritoryIndex, updatedTerritory)
                    : territories;
            setTerritories(updatedTerritories);
        },
        [territories]
    );

    const onTerritoryHover = React.useCallback(territoryId => {
        setHoveredTerritoryId(territoryId);
    }, []);

    const onOutletSelect = React.useCallback(id => {
        setSelectedOutletId(id);
    }, []);

    const onOutletHover = React.useCallback(id => {
        setHoveredOutletId(id);
    }, []);

    const selectedOutlets =
        territories.size !== 0 && selectedTerritoryIndex !== null
            ? territories.get(selectedTerritoryIndex)?.outletsInfo.outlets ||
              Immutable.List()
            : Immutable.List();

    const onTerritorySelect = React.useCallback(
        id => {
            const index = territories.findIndex(territory => {
                return territory.id === id;
            });
            setSelectedTerritoryIndex(index);
            setSelectedTerritoryId(id);
            setSelectedOutletId(null);
        },
        [territories]
    );

    React.useEffect(() => {
        if (!!territories.size && selectedTerritoryIndex === null) {
            setSelectedTerritoryIndex(0);
        }
    }, [territories, selectedTerritoryIndex, setSelectedTerritoryId]);

    const [allOutlets, allOutletIds] = React.useMemo(() => {
        const outlets = territories.reduce(
            ([outlets, outletIds], territory) => {
                if (territory.outletsInfo.outlets) {
                    return [
                        outlets.concat(territory.outletsInfo.outlets),
                        outletIds.concat(
                            territory.outletsInfo.outlets.map(({ id }) => id)
                        )
                    ];
                }
                return [outlets, outletIds];
            },
            [
                Immutable.List(unallocatedOutlets || []),
                Immutable.List((unallocatedOutlets || []).map(({ id }) => id))
            ]
        );

        return outlets;
    }, [territories, unallocatedOutlets]);

    const [showOutletsAddModal, setShowOutletsAddModal] = React.useState();

    React.useEffect(() => {
        if (selectedOutlets?.size) {
            TerritoryActions.getOutletMetrics({
                ids: selectedOutlets.map(({ id }) => id).toJS(),
                clientId: props.clientId
            }).then(allOutletMetrics => {
                setSelectedOutletMetrics(allOutletMetrics);
            });
        }
    }, [props.clientId, selectedOutlets]);

    React.useEffect(() => {
        if (
            props.isManualPlan &&
            !unallocatedOutlets.size &&
            !allOutletIds.size
        ) {
            setShowOutletsAddModal(true);
        }
    }, [
        allOutletIds.size,
        disableNavigation,
        props.isManualPlan,
        unallocatedOutlets.size
    ]);

    const routingParams = useParams();

    const outletsData = useSelector(state =>
        OutletSelectors.getEntities(state, { match: { params: routingParams } })
    );

    const onOutletAddToggle = React.useCallback(() => {
        setShowOutletsAddModal(show => !show);
    }, []);

    const checkAndCloseOutletModel = React.useCallback(() => {
        if (
            props.isManualPlan &&
            !unallocatedOutlets.size &&
            !allOutletIds.size
        ) {
            onDismissTerritoryCreation();
        }
        onOutletAddToggle();
    }, [
        allOutletIds.size,
        onDismissTerritoryCreation,
        onOutletAddToggle,
        props.isManualPlan,
        unallocatedOutlets.size
    ]);

    const _onUnallocatedOutletSelection = React.useCallback(
        selectedIds => {
            const { entities: outlets } = outletsData;
            let duplicateCount = 0;
            const selectedOutlets = outlets.filter(outlet => {
                if (allOutletIds.includes(outlet.id)) {
                    duplicateCount += 1;
                    return false;
                }
                return (
                    selectedIds.selectedRows.includes(outlet.id) &&
                    outlet.locationInfo.location.latLng
                );
            });

            setUnallocatedOutlets(unallocatedOutlets => {
                return Immutable.List(selectedOutlets).concat(
                    unallocatedOutlets
                );
            });
            onOutletAddToggle();
            if (duplicateCount) {
                showToast({
                    title: t('createTerritory.unallocatedDuplicateRemoved', {
                        duplicateCount
                    }),
                    palette: 'primary'
                });
            }
        },
        [allOutletIds, onOutletAddToggle, outletsData, showToast, t]
    );

    const changeTerritory = React.useCallback(
        (originalTerritoryId, newTerritoryId, outletId, unallocatedOutlet) => {
            let editedOutlet;
            let updatedTerritories;
            if (unallocatedOutlet) {
                editedOutlet = unallocatedOutlet;
                updatedTerritories = territories;
                setUnallocatedOutlets(unallocatedOutlets =>
                    unallocatedOutlets.filter(
                        ({ id }) => id !== unallocatedOutlet.id
                    )
                );
            } else {
                updatedTerritories = territories.map(territory => {
                    if (territory.get('id') === originalTerritoryId) {
                        editedOutlet = territory.outletsInfo.outlets.find(
                            outlet => outlet.id === outletId
                        );
                        const updatedOutlets = territory.outletsInfo.outlets.filter(
                            outlet => {
                                return outlet.id !== outletId;
                            }
                        );
                        const updatedOutletsIds = territory.outletsInfo.outletIds.filter(
                            outlet => {
                                return outlet.outletId !== outletId;
                            }
                        );
                        const updatedOutletInfo = territory.outletsInfo
                            .setOutlets(updatedOutlets)
                            .setOutletIds(updatedOutletsIds);
                        const updatedTerritory = territory.setOutletsInfo(
                            updatedOutletInfo
                        );
                        return updatedTerritory;
                    }
                    return territory;
                });
            }
            const newTerritories = updatedTerritories.map(territory => {
                if (territory.id === newTerritoryId) {
                    const updatedOutlets = Immutable.List([
                        ...territory.outletsInfo.outlets,
                        editedOutlet
                    ]);
                    const updatedOutletIds = Immutable.List([
                        ...territory.outletsInfo.outletIds,
                        TerritoryOutletId.fromJS({
                            clientId: editedOutlet.clientId,
                            outletId: editedOutlet.id
                        })
                    ]);
                    const updatedTerritory = territory.setOutletsInfo(
                        territory.outletsInfo
                            .setOutlets(updatedOutlets)
                            .setOutletIds(updatedOutletIds)
                    );
                    return updatedTerritory;
                }
                return territory;
            });
            setTerritories(newTerritories);
        },
        [territories]
    );

    return (
        <TerritoryContext.Provider
            value={{
                territories,
                setTerritories,
                hoveredTerritoryId,
                setHoveredTerritoryId,
                selectedOutletId,
                setLayerColorMap,
                layerColorMap,
                hoveredOutletId,
                selectedOutlets,
                setHoveredOutletId,
                onOutletSelect,
                onOutletHover,
                onTerritorySelect,
                onTerritoryHover,
                selectedTerritoryId,
                allOutlets,
                allOutletIds,
                isEditing: props.isEditing,
                selectedTerritoryIndex,
                setSelectedTerritoryId,
                setSelectedOutletId,
                unallocatedOutlets,
                setUnallocatedOutlets,
                colorPicker,
                isManualPlan: props.isManualPlan,
                toggleAddOutletsModel: onOutletAddToggle,
                selectedOutletMetrics,
                changeTerritoryName,
                changeTerritory,
                clientId: props.clientId,
                planStatus: props.planStatus
            }}
        >
            <>
                {showOutletsAddModal && (
                    <OutletSelection
                        onNext={_onUnallocatedOutletSelection}
                        onDismiss={checkAndCloseOutletModel}
                        disableSelectAll
                    />
                )}
                {props.children}
            </>
        </TerritoryContext.Provider>
    );
};

export default ManualTerritoryProvider;
